import React from 'react';
import { clsx } from 'clsx';
import { ListBulletIcon, Squares2X2Icon } from '@heroicons/react/24/outline';

export type ViewMode = 'card' | 'list';

const isViewMode = (value: unknown): value is ViewMode => value === 'card' || value === 'list';

/** A view mode remembered per device under `storageKey`. Storage can be unavailable, e.g. in private browsing. */
export const usePersistedViewMode = (
  storageKey: string,
  fallback: ViewMode = 'card'
): [ViewMode, (mode: ViewMode) => void] => {
  const [mode, setMode] = React.useState<ViewMode>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      return isViewMode(stored) ? stored : fallback;
    } catch {
      return fallback;
    }
  });
  const update = React.useCallback(
    (next: ViewMode) => {
      setMode(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // The choice still applies for this visit.
      }
    },
    [storageKey]
  );
  return [mode, update];
};

const OPTIONS = [
  { mode: 'card', label: 'Card view', Icon: Squares2X2Icon },
  { mode: 'list', label: 'List view', Icon: ListBulletIcon }
] as const;

export const ViewModeToggle = ({
  value,
  onChange
}: {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
}) => (
  <div className="inline-flex flex-none items-center rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-1">
    {OPTIONS.map(({ mode, label, Icon }) => (
      <button
        key={mode}
        type="button"
        onClick={() => onChange(mode)}
        aria-pressed={value === mode}
        aria-label={label}
        title={label}
        className={clsx(
          'flex-none rounded-md p-1.5 transition-colors',
          value === mode
            ? 'bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-300'
            : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-white/5'
        )}
      >
        <Icon className="h-4 w-4" />
      </button>
    ))}
  </div>
);
